import { get12HourDisplay, LocaleCodeToTimeFormatMap } from '@doss/common-all'
import { useEffect, useRef, useState } from 'react'
import { Input } from '@/components/ui/input-v2'
import { ToggleGroup } from '@/components/ui/toggle-group'
import { cn } from '@/lib/utils'
import { ApplicationOrientation } from '~/common/generated/apollo-operations'
import { useApplicationOrientation } from '~/common/hooks/useApplicationOrientation'
import { useGlobalContext } from '~/common/hooks/useGlobalContext'
import { HourDropdown } from './HourDropdown'
import { MinuteDropdown } from './MinuteDropdown'
import { Period } from './types'

type TimePickerProps = {
  hour: number
  minute: number
  onHourChange: (value: number) => void
  onMinuteChange: (value: number) => void
  /** When true, stretches to fill container width (useful for single date picker) */
  fullWidth?: boolean
}

export function TimePicker({
  hour,
  minute,
  onHourChange,
  onMinuteChange,
  fullWidth,
}: TimePickerProps) {
  const { locale } = useGlobalContext()
  const { orientation } = useApplicationOrientation()
  const timeFormat = LocaleCodeToTimeFormatMap[locale]
  const is24Hour = timeFormat.includes('HH')

  const [showHourDropdown, setShowHourDropdown] = useState(false)
  const [showMinuteDropdown, setShowMinuteDropdown] = useState(false)

  const hourRef = useRef<HTMLDivElement>(null)
  const minuteRef = useRef<HTMLDivElement>(null)

  const [hourInput, setHourInput] = useState(() =>
    is24Hour ? hour.toString().padStart(2, '0') : get12HourDisplay(hour),
  )
  const [minuteInput, setMinuteInput] = useState(minute.toString().padStart(2, '0'))

  const [period, setPeriod] = useState<Period>(hour >= 12 ? Period.PM : Period.AM)

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement
      if (hourRef.current && !hourRef.current.contains(target)) {
        setShowHourDropdown(false)
      }
      if (minuteRef.current && !minuteRef.current.contains(target)) {
        setShowMinuteDropdown(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  useEffect(() => {
    setPeriod(hour >= 12 ? Period.PM : Period.AM)
    setHourInput(is24Hour ? hour.toString().padStart(2, '0') : get12HourDisplay(hour)) // Sync displayed hour when prop changes
  }, [hour, is24Hour])

  useEffect(() => {
    setMinuteInput(minute.toString().padStart(2, '0')) // Sync displayed minute when prop changes
  }, [minute])

  const handleHourChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    if (value === '' || (/^\d*$/.test(value) && parseInt(value, 10) <= (is24Hour ? 23 : 12))) {
      setHourInput(value)
    }
  }

  const handleMinuteChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    if (value === '' || (/^\d*$/.test(value) && parseInt(value, 10) < 60)) {
      setMinuteInput(value) // Update display value without converting immediately
    }
  }

  const handleHourBlur = () => {
    const parsedHour = parseInt(hourInput, 10)

    if (is24Hour) {
      if (!isNaN(parsedHour) && parsedHour >= 0 && parsedHour <= 23) {
        onHourChange(parsedHour)
      } else {
        setHourInput(hour.toString().padStart(2, '0'))
      }
    } else {
      if (!isNaN(parsedHour) && parsedHour >= 1 && parsedHour <= 12) {
        let hour24 = parsedHour
        // Handle if the value entered is 12
        if (parsedHour === 12) {
          hour24 = period === Period.AM ? 0 : 12
        }
        if (period === Period.PM && parsedHour < 12) {
          hour24 += 12
        }
        onHourChange(hour24)
      } else {
        setHourInput(get12HourDisplay(hour)) // Reset to valid value on blur
      }
    }
  }

  const handleMinuteBlur = () => {
    const parsedMinute = parseInt(minuteInput, 10)
    if (!isNaN(parsedMinute) && parsedMinute >= 0 && parsedMinute < 60) {
      onMinuteChange(parsedMinute)
    } else {
      setMinuteInput(minute.toString().padStart(2, '0')) // Reset to valid value on blur
    }
  }

  const onPeriodChange = (newPeriod: Period | undefined) => {
    if (!newPeriod) {
      return
    }

    setPeriod(newPeriod)

    // Update the hour when period is changed
    let hour24 = hour

    if (hour24 === 0) {
      // Changing from 12 AM (00:00) to 12 PM (12:00)
      hour24 = newPeriod === Period.PM ? 12 : 0
    } else if (hour24 === 12) {
      // Changing from 12 PM (12:00) to 12 AM (00:00)
      hour24 = newPeriod === Period.AM ? 0 : 12
    } else if (hour24 < 12) {
      hour24 = newPeriod === Period.PM ? hour24 + 12 : hour24
    } else {
      hour24 = newPeriod === Period.AM ? hour24 - 12 : hour24
    }

    onHourChange(hour24)
  }

  const periodOptions = [
    { label: 'AM', value: Period.AM },
    { label: 'PM', value: Period.PM },
  ]

  let layoutDirection = 'ltr'
  // Hebrew reads time from right to left but the time picker reads from left to right
  if (!locale.startsWith('he-') && orientation === ApplicationOrientation.RIGHT_TO_LEFT) {
    layoutDirection = 'rtl'
  }

  return (
    <div
      className={cn(
        'flex items-center gap-3',
        is24Hour && 'justify-center',
        fullWidth && !is24Hour && 'justify-between',
      )}
      dir={layoutDirection}
    >
      <div className="flex items-center gap-2">
        <div ref={hourRef} className="relative">
          <Input
            className={cn(
              'text-center rounded border border-gray-4 text-xs focus-within:border-none h-[30px]',
              is24Hour ? 'w-[115px]' : 'w-12',
            )}
            onBlur={handleHourBlur}
            onChange={handleHourChange}
            onFocus={() => setShowHourDropdown(true)}
            placeholder={is24Hour ? 'HH' : 'hh'}
            value={hourInput}
          />
          {showHourDropdown && (
            <HourDropdown
              hour={hour}
              is24Hour={is24Hour}
              onClose={() => setShowHourDropdown(false)}
              onHourChange={onHourChange}
              period={period}
            />
          )}
        </div>

        <span className="text-gray-10">:</span>

        <div ref={minuteRef} className="relative">
          <Input
            className={cn(
              'text-center rounded border border-gray-4 text-xs focus-within:border-none h-[30px]',
              is24Hour ? 'w-[115px]' : 'w-12',
            )}
            onBlur={handleMinuteBlur}
            onChange={handleMinuteChange}
            onFocus={() => setShowMinuteDropdown(true)}
            placeholder="MM"
            value={minuteInput}
          />
          {showMinuteDropdown && (
            <MinuteDropdown
              minute={minute}
              onClose={() => setShowMinuteDropdown(false)}
              onMinuteChange={onMinuteChange}
            />
          )}
        </div>
      </div>

      {!is24Hour && (
        <div>
          <ToggleGroup<Period>
            className="h-8"
            items={periodOptions}
            onValueChange={onPeriodChange}
            type="single"
            value={period}
          />
        </div>
      )}
    </div>
  )
}
